import React, { FC, useState } from "react";
import { Avatar, Button, message, Modal, Space, Table, Tag, Tooltip, Typography } from "antd";
import { AppstoreOutlined, CloseOutlined, FilePdfOutlined, PrinterOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { PDFDocument, type PDFPage } from "pdf-lib";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateTrackingPrint } from "hooks/useOrders";
import { IFactoryOrderTrackRow, type PartnerTransportBrand } from "services/interface";
import { getTpLabelUrl } from "services/ordersApi";
import { formatDate, formatDateWithTime } from "utils/dateFormat";
import { numberFormat } from "utils/nuberFormat";
import PdfViewerModal from "./PdfViewerModal";
import { createShippingLabelPdfBase64, type ShippingLabelData } from "./createShippingLabelPdf";

/** จำกัดจำนวนรายการที่รวมเป็น PDF เดียว — ป้องกันโหลดหนักและ CORS/timeout */
const MAX_MERGE_COUNT = 100;

/** ตัดคำว่า "_SPEED" ออกจาก logo URL (ให้สอดคล้องกับ SKUModalForm) */
const normalizeSpeedLogo = (logo: string | null | undefined): string | undefined => {
  if (logo === null || logo === undefined) return undefined;
  const s = String(logo).replace(/_SPEED/gi, "").trim();
  return s ? s : undefined;
};

interface TrackTableListProps {
  data: IFactoryOrderTrackRow[];
  loading: boolean;
  totalRecord: number;
  pageNumber: number;
  rowPerPage: number;
  onPageChange: (page: number, pageSize?: number) => void;
  refetch: () => void;
  /** true = แท็บส่งกับแบรนด์ (โชว์ปุ่มแบรนด์และคอลัมน์แบรนด์), false = แท็บส่งเอง (ไม่โชว์) */
  showBrandFilter: boolean;
  /** รายการแบรนด์จาก component แม่ — ใช้เป็นปุ่มเลือกแบรนด์ (พร้อมโลโก้) แถวเดียวกับปุ่ม ปริ้นทั้งหมด */
  brandList: PartnerTransportBrand[];
  /** เลือกได้หลายแบรนด์ — ส่งไป API เป็น brand_codes (array) */
  selectedBrandCodes: string[];
  setSelectedBrandCodes: (value: string[] | ((prev: string[]) => string[])) => void;
}

const TrackTableList: FC<TrackTableListProps> = ({
  data,
  loading,
  totalRecord,
  pageNumber,
  rowPerPage,
  onPageChange,
  refetch,
  showBrandFilter,
  brandList,
  selectedBrandCodes,
  setSelectedBrandCodes,
}) => {
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);
  const [selectedPdfTitle, setSelectedPdfTitle] = useState<string>("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  /** หลัง merge PDF แล้ว เปิด Modal แสดง preview หลายหน้า (เหมือน PdfViewerModal) */
  const [mergedBlobUrl, setMergedBlobUrl] = useState<string | null>(null);
  const [mergePreviewOpen, setMergePreviewOpen] = useState(false);
  const [pendingPrintKeys, setPendingPrintKeys] = useState<string[]>([]);
  /** tracking.id ที่กำลังอัปเดตสถานะพิมพ์อยู่ (ปุ่ม "ดู PDF" แถวนั้นจะโชว์ loading) */
  const [updatingTrackingId, setUpdatingTrackingId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { mutateAsync: updateTrackingPrintMutate, isPending: isUpdatingTrackingPrint } = useUpdateTrackingPrint();

  /** หลังอัปเดตสถานะพิมพ์แล้ว ให้ refetch รายการแบรนด์เพื่ออัปเดต label_wait_print_count */
  const invalidateBrands = () => queryClient.invalidateQueries({ queryKey: ["transportBrands"] });

  /** ค่า brand ที่ใช้เปรียบเทียบ (brand_code หรือ id) */
  const getBrandValue = (b: PartnerTransportBrand) =>
    (b.brand_code != null && b.brand_code !== "") ? b.brand_code : b.id;

  /** กดปุ่ม "ทั้งหมด" → ล้างการเลือกแบรนด์ (ส่ง [] ไปหลังบ้าน) */
  const handleSelectAllBrands = () => setSelectedBrandCodes([]);

  /** กดปุ่มแบรนด์ → toggle ใน array (เลือกได้มากกว่าหนึ่ง) */
  const handleToggleBrand = (value: string) => {
    setSelectedBrandCodes((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  /** เปิด Modal ดู PDF (ใช้เมื่อไม่ต้องอัปเดตสถานะ) */
  const openPdfModal = (record: IFactoryOrderTrackRow) => {
    const imageUrl = record.tracking?.image_url;
    if (imageUrl) {
      setSelectedPdfUrl(imageUrl);
      setSelectedPdfTitle(
        [record.item?.order_code, record.tracking?.tracking_number].filter(Boolean).join(" - ") || "Label"
      );
      setPdfModalOpen(true);
    }
  };

  /** กด "ดู PDF" → เปิด Modal preview ก่อน แล้วมาร์กสถานะพิมพ์หลังกดพิมพ์จริงใน Modal — แท็บส่งกับแบรนด์ */
  const handleViewPdf = async (record: IFactoryOrderTrackRow) => {
    const trackingId = record.tracking?.id;
    let imageUrl = record.tracking?.image_url;
    if (!trackingId) return;
    // ถ้า image_url เป็น null และเป็น THAIPOST → ดึงสดจาก API ก่อน
    if (!imageUrl && record.tracking?.carrier_code === 'THAIPOST') {
      const barcode = record.tracking?.tracking_number;
      if (!barcode) return;
      setUpdatingTrackingId(trackingId);
      try {
        const result = await getTpLabelUrl(barcode);
        imageUrl = result.image_url;
      } catch (e) {
        message.error('ไม่สามารถดึง Label ได้ กรุณาลองใหม่อีกครั้ง');
        setUpdatingTrackingId(null);
        return;
      }
    }
    if (!imageUrl) return;
    setUpdatingTrackingId(trackingId);
    try {
      // ตั้ง pendingPrintKeys เพื่อให้ handleSinglePdfAfterPrint มาร์กสถานะหลังผู้ใช้กดพิมพ์จริง
      setPendingPrintKeys([trackingId]);
      setSelectedPdfUrl(imageUrl);
      setSelectedPdfTitle(
        [record.item?.order_code, record.tracking?.tracking_number].filter(Boolean).join(" - ") || "Label"
      );
      setPdfModalOpen(true);
    } finally {
      setUpdatingTrackingId(null);
    }
  };

  /** แท็บส่งเอง: กด "ดู PDF" → สร้าง PDF จาก createShippingLabelPdfBase64 แล้วเปิด Modal — มาร์กสถานะหลังกดพิมพ์จริงใน Modal */
  const handleViewPdfSelf = async (record: IFactoryOrderTrackRow) => {
    const trackingId = record.tracking?.id;
    if (!trackingId) return;
    setUpdatingTrackingId(trackingId);
    try {
      const labelData = buildLabelDataFromRecord(record);
      const base64 = await createShippingLabelPdfBase64(labelData);
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      // ตั้ง pendingPrintKeys เพื่อให้ handleSinglePdfAfterPrint มาร์กสถานะหลังผู้ใช้กดพิมพ์จริง
      setPendingPrintKeys([trackingId]);
      setSelectedPdfUrl(blobUrl);
      setSelectedPdfTitle(
        [record.item?.order_code, record.tracking?.tracking_number].filter(Boolean).join(" - ") || "Label (ส่งเอง)"
      );
      setPdfModalOpen(true);
    } catch (e) {
      message.error("สร้าง PDF ไม่สำเร็จ");
      console.error(e);
    } finally {
      setUpdatingTrackingId(null);
    }
  };

  /** เรียกหลังผู้ใช้กดพิมพ์จริงใน single PDF Modal — มาร์ก is_print ให้ตรงกับ pendingPrintKeys */
  const handleSinglePdfAfterPrint = () => {
    if (!pendingPrintKeys.length) return;
    updateTrackingPrintMutate({ tracking_ids: pendingPrintKeys })
      .then(() => {
        refetch();
        invalidateBrands();
      })
      .catch(() => message.warning('ส่งพิมพ์แล้ว แต่มาร์กสถานะไม่สำเร็จ'));
  };

  const closePdfModal = () => {
    if (selectedPdfUrl && selectedPdfUrl.startsWith("blob:")) {
      URL.revokeObjectURL(selectedPdfUrl);
    }
    setPdfModalOpen(false);
    setSelectedPdfUrl(null);
    setSelectedPdfTitle("");
    setPendingPrintKeys([]); // ล้าง pendingPrintKeys เมื่อปิด Modal โดยไม่พิมพ์
  };

  /** เช็คว่า image_url เป็นลิงก์ที่ใช้เปิด PDF ได้หรือไม่ */
  const isValidPdfUrl = (url: string | null | undefined): boolean => {
    if (!url || typeof url !== "string") return false;
    const trimmed = url.trim();
    return trimmed.startsWith("http://") || trimmed.startsWith("https://");
  };

  const getLabelUnavailableReason = (url: string | null | undefined): string => {
    if (!url) return "ไม่มี URL ของ Label";
    if (typeof url !== "string") return "ฟอร์แมตไม่ถูกต้อง";
    const trimmed = url.trim();
    if (!trimmed) return "ไม่มี URL ของ Label";
    if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) return "ฟอร์แมตไม่ถูกต้อง (ต้องเป็นลิงก์ http หรือ https)";
    return "ไม่พร้อมใช้งาน";
  };

  /** แท็บส่งเอง: สร้าง PDF แต่ละรายการจาก createShippingLabelPdfBase64 แล้ว merge เป็นไฟล์เดียว เปิด Modal */
  const buildLabelDataFromRecord = (record: IFactoryOrderTrackRow): ShippingLabelData => {
    const isBkk = record.address?.province_name?.includes('กรุงเทพ');
    const addPrefix = (val: string | null | undefined, prefix: string) => {
      if (!val) return undefined;
      if (/^(ที่อยู่|แขวง|เขต|ต\.|อ\.|จ\.|ตำบล|อำเภอ|จังหวัด)/.test(val)) return val;
      return `${prefix}${val}`;
    };

    return {
      factoryName: record.order_item?.factory_name ?? undefined,
      factoryCode: record.order_item?.factory_code ?? undefined,
      factoryPhone: record.order_item?.factory_tel ?? undefined,
      orderCode: record.item?.order_code ?? "",
      trackingNumber: record.tracking?.tracking_number ?? undefined,
      productName: record.item?.product_name ?? "",
      productSku: record.sku_variant?.sku_value ?? record.item?.sku_description ?? undefined,
      recipientName: record.address?.address_name ?? undefined,
      recipientPhone: record.address?.address_telephone ?? undefined,
      addressLine1: addPrefix(
        [record.address?.address_line_1, record.address?.address_line_2].filter(Boolean).join(' ') || record.address?.address_text || undefined, 
        'ที่อยู่ '
      ),
      addressLine2: [
        addPrefix(record.address?.sub_district_name, isBkk ? 'แขวง ' : 'ตำบล '),
        addPrefix(record.address?.district_name, isBkk ? 'เขต ' : 'อำเภอ '),
      ].filter(Boolean).join(' ') || undefined,
      addressLine4: [
        addPrefix(record.address?.province_name, isBkk ? '' : 'จังหวัด '), 
        record.address?.zipcode ? String(record.address.zipcode) : undefined
      ].filter(Boolean).join(' ') || undefined,
    };
  };

  const executePrintAllSelf = async () => {
    const keys = selectedRowKeys as string[];
    if (!keys.length) return;
    const list = data || [];
    const selectedRecords = keys
      .map((id) => list.find((r) => r.tracking?.id === id))
      .filter((r): r is IFactoryOrderTrackRow => !!r);
    if (!selectedRecords.length) {
      message.warning("ไม่มีรายการที่เลือกได้");
      return;
    }
    if (selectedRecords.length > MAX_MERGE_COUNT) {
      message.warning(`รวมได้สูงสุด ${MAX_MERGE_COUNT} รายการ กรุณาลดการเลือก`);
      return;
    }
    setMergeLoading(true);
    try {
      const base64List = await Promise.all(
        selectedRecords.map((record) =>
          createShippingLabelPdfBase64(buildLabelDataFromRecord(record))
        )
      );
      const mergedPdf = await PDFDocument.create();
      for (const base64 of base64List) {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const pdf = await PDFDocument.load(bytes);
        const pageIndices = pdf.getPageIndices();
        const copiedPages = await mergedPdf.copyPages(pdf, pageIndices);
        copiedPages.forEach((page: PDFPage) => mergedPdf.addPage(page));
      }
      const mergedBytes = await mergedPdf.save();
      const blob = new Blob([new Uint8Array(mergedBytes)], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      setMergedBlobUrl(blobUrl);
      setMergePreviewOpen(true);
      setPendingPrintKeys(keys);

      // ดาวน์โหลดเป็นไฟล์ .pdf ด้วย (ไม่พึ่งแค่ blob ใน Modal)
      const fileName = `shipping-labels-ส่งเอง-${selectedRecords.length}-รายการ-${new Date().toISOString().slice(0, 10)}.pdf`;
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      link.click();
      message.success("ดาวน์โหลดไฟล์ PDF แล้ว และเปิด preview ด้านบน");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "สร้าง/รวม PDF ไม่สำเร็จ";
      message.error(msg);
      console.error(err);
    } finally {
      setMergeLoading(false);
    }
  };

  /** รวม PDF ที่เลือกเป็นไฟล์เดียว แล้วเปิด Modal แสดง preview หลายหน้า (แบบ PdfViewerModal) — โหลด PDF แบบ parallel แล้วค่อย merge ตามลำดับ */
  const executePrintAll = async () => {
    const keys = selectedRowKeys as string[];
    if (!keys.length) return;
    const list = data || [];
    const selectedRecords = keys
      .map((id) => list.find((r) => r.tracking?.id === id))
      .filter((r): r is IFactoryOrderTrackRow => !!r);

    // resolve THAIPOST records ที่ยังไม่มี image_url ก่อน
    const resolvedRecords: IFactoryOrderTrackRow[] = await Promise.all(
      selectedRecords.map(async (r) => {
        if (!r.tracking?.image_url && r.tracking?.carrier_code === 'THAIPOST' && r.tracking?.tracking_number) {
          try {
            const result = await getTpLabelUrl(r.tracking.tracking_number);
            return { ...r, tracking: { ...r.tracking, image_url: result.image_url } };
          } catch {
            return r; // ดึงไม่ได้ → ข้ามรายการนี้ตอน filter ด้านล่าง
          }
        }
        return r;
      })
    );

    const printableRecords = resolvedRecords.filter((r) => isValidPdfUrl(r.tracking?.image_url));
    const urls = printableRecords.map((r) => r.tracking!.image_url!);
    if (!urls.length) {
      message.warning("ไม่มี Label ที่เลือกได้");
      return;
    }
    if (urls.length > MAX_MERGE_COUNT) {
      message.warning(`รวมได้สูงสุด ${MAX_MERGE_COUNT} รายการ กรุณาลดการเลือก`);
      return;
    }
    setMergeLoading(true);
    try {
      /** โหลด PDF ทุก URL พร้อมกัน (parallel) — ลดเวลารอเมื่อเลือกหลายรายการ */
      const buffers = await Promise.all(
        urls.map(async (url) => {
          const res = await fetch(url);
          if (!res.ok) throw new Error(`โหลด PDF ไม่สำเร็จ: ${url}`);
          return res.arrayBuffer();
        })
      );
      const mergedPdf = await PDFDocument.create();
      for (const bytes of buffers) {
        const pdf = await PDFDocument.load(bytes);
        const pageIndices = pdf.getPageIndices();
        const copiedPages = await mergedPdf.copyPages(pdf, pageIndices);
        copiedPages.forEach((page: PDFPage) => mergedPdf.addPage(page));
      }
      const mergedBytes = await mergedPdf.save();
      const blob = new Blob([new Uint8Array(mergedBytes)], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      setMergedBlobUrl(blobUrl);
      setMergePreviewOpen(true);
      setPendingPrintKeys(keys);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "รวม PDF ไม่สำเร็จ (อาจติด CORS หรือลิงก์ไม่ถูกต้อง)";
      message.error(msg);
    } finally {
      setMergeLoading(false);
    }
  };

  /** ปิด Modal preview หลายหน้า และ revoke blob URL */
  const closeMergePreview = () => {
    if (mergedBlobUrl) URL.revokeObjectURL(mergedBlobUrl);
    setMergePreviewOpen(false);
    setMergedBlobUrl(null);
    setPendingPrintKeys([]);
  };

  /** หลังกดพิมพ์ใน Modal preview แล้ว: มาร์ก is_print, refetch, ล้าง selection, ปิด Modal */
  const handleMergePreviewAfterPrint = () => {
    const keys = pendingPrintKeys;
    updateTrackingPrintMutate({ tracking_ids: keys })
      .then((result) => {
        refetch();
        invalidateBrands();
        setSelectedRowKeys([]);
        if (result === "success") {
          message.success("มาร์กสถานะพิมพ์แล้วครบทุกรายการ");
        } else if (typeof result === "object" && "updated_count" in result) {
          if (result.updated_count === 0) {
            message.warning("ไม่มีรายการที่อัปเดตสถานะพิมพ์ได้");
          } else {
            message.success(`มาร์กสถานะพิมพ์แล้ว ${result.updated_count} รายการ`);
          }
        }
      })
      .catch(() => {
        message.warning("ส่งไปพิมพ์แล้ว แต่มาร์กสถานะพิมพ์ไม่สำเร็จ");
      });
    if (mergedBlobUrl) URL.revokeObjectURL(mergedBlobUrl);
    setMergePreviewOpen(false);
    setMergedBlobUrl(null);
    setPendingPrintKeys([]);
  };

  const handlePrintAll = () => {
    if (selectedRowKeys.length === 0) return;
    if (selectedRowKeys.length > MAX_MERGE_COUNT) {
      message.warning(`รวมได้สูงสุด ${MAX_MERGE_COUNT} รายการ กรุณาลดการเลือก`);
      return;
    }
    const keys = selectedRowKeys as string[];
    const isSelf = !showBrandFilter;
    Modal.confirm({
      title: "ปริ้นทั้งหมด",
      content: (
        <>
          ต้องการรวม Label ที่เลือก <strong>{selectedRowKeys.length}</strong> รายการ เป็นไฟล์ PDF เดียวแล้วส่งไปพิมพ์ใช่หรือไม่?
          <br />
         {/* <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {isSelf
              ? `(ส่งเอง — สร้าง Label จากข้อมูลแต่ละรายการแล้วรวมเป็น PDF เดียว รวมได้สูงสุด ${MAX_MERGE_COUNT} รายการ)`
              : `หลังส่งไปพิมพ์แล้ว ระบบจะมาร์กว่าพิมพ์แล้ว ให้อัตโนมัติ (รวมได้สูงสุด ${MAX_MERGE_COUNT} รายการ)`}
          </Typography.Text> */}
        </>
      ),
      okText: "ยืนยัน",
      cancelText: "ยกเลิก",
      onOk: async () => {
        if (isSelf) {
          await executePrintAllSelf();
        } else {
          // ไม่อัปเดตสถานะพิมพ์ที่นี่ — handleMergePreviewAfterPrint จะจัดการหลังกดพิมพ์จริงใน Modal
          await executePrintAll();
        }
      },
    });
  };

  /** row สามารถเลือกได้ถ้ามี image_url หรือเป็น THAIPOST ที่มีเลขพัสดุ (lazy load จะดึง URL ตอนพิมพ์) */
  const isRowSelectable = (record: IFactoryOrderTrackRow): boolean => {
    if (!showBrandFilter) return true;
    if (isValidPdfUrl(record.tracking?.image_url)) return true;
    return record.tracking?.carrier_code === 'THAIPOST' && !!record.tracking?.tracking_number;
  };

  const getSelectableRowKeys = (): React.Key[] => {
    if (!data) return [];
    return data
      .filter((record) => isRowSelectable(record))
      .map((record) => record.tracking.id);
  };

  const rowSelection: {
    selectedRowKeys: React.Key[];
    onChange: (keys: React.Key[]) => void;
    getCheckboxProps: (record: IFactoryOrderTrackRow) => { disabled: boolean };
  } = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: !isRowSelectable(record),
    }),
  };

  const columns: ColumnsType<IFactoryOrderTrackRow> = [
    {
      title: "Order No.",
      key: "order_code",
      width: 100,
      ellipsis: true,
      render: (_: unknown, record: IFactoryOrderTrackRow) => {
        const text = record.item?.order_code ?? "-";
        return (
          <Typography.Text strong ellipsis={{ tooltip: text }} style={{ fontSize: 12 }}>
            {text}
          </Typography.Text>
        );
      },
    },
    {
      title: "ชื่อ / ที่อยู่",
      key: "ref_name_address",
      width: 110,
      ellipsis: true,
      render: (_: unknown, record: IFactoryOrderTrackRow) => {
        const name = record.ref?.ref_name ?? record.address?.address_name ?? "-";
        const address = record.address?.address_text ?? "-";
        const fullText = address !== "-" ? `${name}\n${address}` : name;
        return (
          <Typography.Text ellipsis={{ tooltip: fullText }} style={{ fontSize: 12 }}>
            <span style={{ display: "block", fontWeight: 500 }}>{name}</span>
            {address !== "-" && (
              <span style={{ display: "block", fontSize: 11, color: "rgba(0,0,0,0.6)" }}>{address}</span>
            )}
          </Typography.Text>
        );
      },
    },
    {
      title: "เบอร์โทรศัพท์",
      key: "ref_telephone",
      width: 90,
      ellipsis: true,
      render: (_: unknown, record: IFactoryOrderTrackRow) => {
        const text = record.ref?.ref_telephone ?? "-";
        return (
          <Typography.Text ellipsis={{ tooltip: text }} style={{ fontSize: 12 }}>
            {text}
          </Typography.Text>
        );
      },
    },
    // {
    //   title: "Carrier",
    //   key: "carrier_code",
    //   width: 100,
    //   render: (_: unknown, record: IFactoryOrderTrackRow) => record.tracking?.carrier_code ?? "-",
    // },
    {
      title: "แบรนด์   ",
      key: "brand",
      width: 56,
      ellipsis: true,
      render: (_: unknown, record: IFactoryOrderTrackRow) => {
        const rawLogo = record.tracking?.brand_logo;
        const logo = normalizeSpeedLogo(rawLogo);
        const name = record.tracking?.brand_name ?? "-";
        if (!logo && !record.tracking?.brand_name) return <Typography.Text type="secondary" style={{ fontSize: 12 }}>-</Typography.Text>;
        return (
          <Space size={4} align="center" style={{ width: "100%", minWidth: 0 }}>
            {logo && <Avatar src={logo} alt={name} shape="square" size={20} style={{ flexShrink: 0 }} />}
            <Typography.Text ellipsis={{ tooltip: name }} style={{ fontSize: 11, minWidth: 0 }}>{name}</Typography.Text>
          </Space>
        );
      },
    },
    {
      title: "เลขพัสดุ",
      key: "tracking_number",
      width: 88,
      ellipsis: true,
      render: (_: unknown, record: IFactoryOrderTrackRow) => {
        const text = record.tracking?.tracking_number ?? "-";
        return (
          <Typography.Text ellipsis={{ tooltip: text }} style={{ fontSize: 12 }}>
            {text}
          </Typography.Text>
        );
      },
    },
    // {
    //   title: "สถานะ",
    //   key: "status",
    //   width: 100,
    //   render: (_: unknown, record: IFactoryOrderTrackRow) => record.tracking?.status ?? "-",
    // },
    {
      title: "สินค้า / SKU",
      key: "product_sku",
      width: 110,
      ellipsis: true,
      render: (_: unknown, record: IFactoryOrderTrackRow) => {
        const imageUrl = record.sku_variant?.image_url ?? record.item?.image_url ?? null;
        const productName = record.item?.product_name ?? "";
        const skuText = record.sku_variant?.sku_value ?? record.item?.sku_description ?? "";
        const line1 = productName || "-";
        const line2 = skuText ? `SKU: ${skuText}` : "";
        const fullText = line2 ? `${line1}\n${line2}` : line1;
        return (
          <Space size={6} align="start" style={{ width: "100%", minWidth: 0 }}>
            {imageUrl && <Avatar src={imageUrl} alt={line1} shape="square" size={28} style={{ flexShrink: 0 }} />}
            <Typography.Text ellipsis={{ tooltip: fullText }} style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
              {line2 ? (
                <>
                  <span style={{ display: "block" }}>{line1}</span>
                  <span style={{ display: "block", fontSize: 11, color: "rgba(0,0,0,0.6)" }}>{line2}</span>
                </>
              ) : (
                line1
              )}
            </Typography.Text>
          </Space>
        );
      },
    },
    {
      title: "จำนวน",
      key: "item_qty",
      width: 48,
      align: "right",
      render: (_: unknown, record: IFactoryOrderTrackRow) => {
        const val = record.order_item?.item_qty;
        return <Typography.Text style={{ fontSize: 12 }}>{val != null ? val : "-"}</Typography.Text>;
      },
    },
    {
      title: "วันที่สร้าง",
      key: "create_date",
      width: 78,
      ellipsis: true,
      render: (_: unknown, record: IFactoryOrderTrackRow) => {
        const str = record.tracking?.create_date ? formatDateWithTime(record.tracking.create_date) : "-";
        return <Typography.Text ellipsis={{ tooltip: str }} style={{ fontSize: 12 }}>{str}</Typography.Text>;
      },
    },
    // {
    //   title: "ค่าขนส่ง",
    //   key: "charge",
    //   width: 100,
    //   align: "right",
    //   render: (_: unknown, record: IFactoryOrderTrackRow) => {
    //     const val = record.tracking?.charge;
    //     return val != null ? numberFormat(val) : "-";
    //   },
    // },
    {
      title: "พิมพ์",
      key: "is_print",
      width: 64,
      render: (_: unknown, record: IFactoryOrderTrackRow) => {
        const isUpdatingThis = updatingTrackingId === record.tracking?.id;
        if (isUpdatingThis || (isUpdatingTrackingPrint && selectedRowKeys.includes(record.tracking?.id))) {
          return <Tag color="processing" style={{ margin: 0, fontSize: 11 }}>กำลังอัปเดต...</Tag>;
        }
        const isPrint = record.tracking?.is_print ?? false;
        return isPrint ? (
          <Tag color="green" style={{ margin: 0, fontSize: 11 }}>ปริ้นแล้ว</Tag>
        ) : (
          <Tag color="default" style={{ margin: 0, fontSize: 11 }}>ยังไม่พิมพ์</Tag>
        );
      },
    },
    {
      title: "Label",
      key: "view_label",
      width: 64,
      fixed: "right" as const,
      render: (_: unknown, record: IFactoryOrderTrackRow) => {
        const isUpdating = updatingTrackingId === record.tracking?.id;
        if (!showBrandFilter) {
          return (
            <Button
              type="link"
              size="small"
              //icon={<FilePdfOutlined />}
              loading={isUpdating}
              onClick={() => handleViewPdfSelf(record)}
              disabled={isUpdating}
              style={{ padding: 0, height: "auto", fontSize: 12 }}
            >
              {isUpdating ? "..." : <PrinterOutlined />}
            </Button>
          );
        }
        const imageUrl = record.tracking?.image_url;
        const valid = isValidPdfUrl(imageUrl);
        const isThaiPost = record.tracking?.carrier_code === 'THAIPOST';
        // THAIPOST ที่ยังไม่มี image_url → ให้กดพิมพ์ได้ (จะดึงสดเมื่อกด)
        if (valid || (!imageUrl && isThaiPost && record.tracking?.tracking_number)) {
          return (
            <Button
              type="link"
              size="small"
              loading={isUpdating}
              onClick={() => handleViewPdf(record)}
              disabled={isUpdating || isUpdatingTrackingPrint}
              style={{ padding: 0, height: "auto", fontSize: 12 }}
            >
              {isUpdating ? "..." : <PrinterOutlined />}
            </Button>
          );
        }
        const reason = getLabelUnavailableReason(imageUrl);
        return (
          <Tooltip title={reason}>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>ไม่พร้อม</Typography.Text>
          </Tooltip>
        );
      },
    },
  ];

  /** แท็บส่งเองไม่โชว์คอลัมน์แบรนด์ */
  const tableColumns = showBrandFilter ? columns : columns.filter((col) => col.key !== "brand");

  return (
    <>
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12, width: "100%", gap: 12 }}>
      {/* ส่วนซ้าย: ปุ่มเลือกแบรนด์ — เฉพาะแท็บส่งกับแบรนด์ */}
      {showBrandFilter && (
        <div style={{ flex: 1, minWidth: 0 }}>
          <Space size={8} wrap>
            <Button
              type={selectedBrandCodes.length === 0 ? "primary" : "default"}
              size="small"
              icon={<AppstoreOutlined />}
              onClick={handleSelectAllBrands}
            >
              ทั้งหมด
            </Button>
            {brandList.map((b) => {
              const value = getBrandValue(b);
              const logo = normalizeSpeedLogo(b.brand_logo);
              const isSelected = selectedBrandCodes.includes(value);
              const waitCount = b.label_wait_print_count ?? 0;
              return (
                <Button
                  key={value}
                  type={isSelected ? "primary" : "default"}
                  size="small"
                  icon={logo ? <Avatar src={logo} alt={b.brand_name} shape="square" size={20} style={{ flexShrink: 0 }} /> : undefined}
                  onClick={() => handleToggleBrand(value)}
                >
                  {b.brand_name}
                  {waitCount > 0 && (
                    <span
                      style={{
                        marginLeft: 6,
                        background: "#ff4d4f",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "0 6px",
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: "18px",
                      }}
                    >
                      {waitCount}
                    </span>
                  )}
                </Button>
              );
            })}
            {selectedBrandCodes.length > 0 && (
              <Button
                type="default"
                size="small"
                danger
                icon={<CloseOutlined />}
                onClick={handleSelectAllBrands}
              >
                ล้าง
              </Button>
            )}
          </Space>
        </div>
      )}
      {/* ส่วนขวา: ชิดขวา — แท็บส่งเองไม่มีปุ่มแบรนด์ จึงใช้ flex-end ให้ปุ่มปริ้นทั้งหมดอยู่ขวา */}
      <div style={{ display: "flex", justifyContent: showBrandFilter ? undefined : "flex-end", flex: showBrandFilter ? undefined : 1 }}>
        <Space size={8} style={{ flexShrink: 0 }}>
          {selectedRowKeys.length === 0 ? (
            <Button
              type="primary"
              disabled={getSelectableRowKeys().length === 0}
              onClick={() => {
                setSelectedRowKeys(getSelectableRowKeys());
              }}
            >
              เลือกทั้งหมด
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrintAll}
              loading={mergeLoading}
            >
              พิมพ์ทั้งหมด {selectedRowKeys.length} รายการ
            </Button>
          )}
        </Space>
      </div>
    </div>
    <Table<IFactoryOrderTrackRow>
      rowKey={(record) => record.tracking.id}
      rowSelection={rowSelection}
      columns={tableColumns}
      dataSource={data || []}
      loading={loading}
      tableLayout="fixed"
      pagination={{
        current: pageNumber,
        pageSize: rowPerPage,
        total: totalRecord,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        showTotal: (total) => `ทั้งหมด ${total} รายการ`,
        onChange: onPageChange,
      }}
      scroll={{ x: 790 }}
      size="small"
    />
    <Modal
      title={`Label - ${selectedPdfTitle}`}
      open={pdfModalOpen}
      onCancel={closePdfModal}
      footer={null}
      width={560}
      style={{ top: 24 }}
      destroyOnClose
    >
      <PdfViewerModal pdfUrl={selectedPdfUrl} title={selectedPdfTitle} onAfterPrint={handleSinglePdfAfterPrint} />
    </Modal>
    <Modal
      title={`Label รวม (${pendingPrintKeys.length} รายการ — หลายหน้า)`}
      open={mergePreviewOpen}
      onCancel={closeMergePreview}
      footer={null}
      width={560}
      style={{ top: 24 }}
      destroyOnClose
    >
      <PdfViewerModal
        pdfUrl={mergedBlobUrl}
        onAfterPrint={handleMergePreviewAfterPrint}
        showAllPagesOption
        defaultViewMode="all"
      />
    </Modal>
    </>
  );
};

export default TrackTableList;
